import React, { useMemo, useState } from 'react';
import { Card, Empty, Select, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Column } from '@ant-design/charts';
import type { EconomyRow } from '@/types';
import { fmtNum } from '../bq-insights/bqMath';
import { sumOf } from './monetizationMath';
import type { TypedEntry } from './monetizationTabState';
import { titleWithInfo, totalRow } from './tableHelpers';
import { TabBody } from './TabBody';

const { Text } = Typography;

export const ALL_CURRENCIES = 'all';
export const NO_AMOUNT_INFO = 'events with no amount (store spends on 0.4.x)';

const columns: ColumnsType<EconomyRow> = [
  { title: 'Currency', dataIndex: 'currency' },
  {
    title: 'Direction',
    dataIndex: 'direction',
    render: (d: string) => <Tag color={d === 'Earned' ? 'green' : 'volcano'}>{d}</Tag>,
  },
  { title: 'Source / sink', dataIndex: 'sourceSink' },
  {
    title: 'Paid',
    dataIndex: 'paid',
    render: (p: boolean) => (p ? <Tag color="gold">paid</Tag> : 'free'),
  },
  { title: 'Amount', dataIndex: 'amount', align: 'right', render: (v) => fmtNum(v) },
  { title: 'Events', dataIndex: 'events', align: 'right', render: (v) => fmtNum(v) },
  {
    title: titleWithInfo('Events without amount', NO_AMOUNT_INFO),
    dataIndex: 'eventsNoAmount',
    align: 'right',
    render: (v) => fmtNum(v),
  },
];

const rowKey = (r: EconomyRow) => `${r.currency}|${r.direction}|${r.sourceSink}|${r.paid}`;

interface ContentProps {
  rows: EconomyRow[];
  /** Initial currency filter; the user changes it with the select. */
  initialCurrency?: string;
}

const Content: React.FC<ContentProps> = ({ rows, initialCurrency = ALL_CURRENCIES }) => {
  const [currency, setCurrency] = useState(initialCurrency);
  const currencies = useMemo(() => [...new Set(rows.map((r) => r.currency))].sort(), [rows]);
  const shown = currency === ALL_CURRENCIES ? rows : rows.filter((r) => r.currency === currency);

  // One currency: each source/sink split into earned (sources) and spent (sinks). All currencies:
  // earned against spent per currency, since amounts of different currencies are not added.
  const chart = useMemo(() => {
    const byKey = new Map<string, { x: string; direction: string; value: number }>();
    for (const r of shown) {
      if (r.amount === null) continue;
      const x = currency === ALL_CURRENCIES ? r.currency : r.sourceSink;
      const k = `${x}|${r.direction}`;
      const cur = byKey.get(k);
      if (cur) cur.value += r.amount;
      else byKey.set(k, { x, direction: r.direction, value: r.amount });
    }
    return [...byKey.values()];
  }, [shown, currency]);

  if (rows.length === 0) {
    return (
      <Empty description="No economy events in this range" image={Empty.PRESENTED_IMAGE_SIMPLE} />
    );
  }
  return (
    <div>
      <Space style={{ marginBottom: 12 }} wrap>
        <Text type="secondary">Currency</Text>
        <Select
          aria-label="Currency"
          style={{ minWidth: 180 }}
          value={currency}
          onChange={setCurrency}
          options={[
            { value: ALL_CURRENCIES, label: 'All currencies' },
            ...currencies.map((c) => ({ value: c, label: c })),
          ]}
        />
      </Space>
      <Card
        size="small"
        title={
          currency === ALL_CURRENCIES
            ? 'Sources vs sinks per currency (earned vs spent)'
            : `Sources vs sinks: ${currency}`
        }
        style={{ marginBottom: 12 }}
      >
        <div data-chart-wrap="economy">
          <Column
            data={chart}
            xField="x"
            yField="value"
            colorField="direction"
            group
            height={240}
            legend={{ color: { position: 'top' as const } }}
            axis={{ y: { labelFormatter: (v: number) => fmtNum(Number(v)) } }}
            animate={false}
          />
        </div>
      </Card>
      <Table<EconomyRow>
        size="small"
        rowKey={rowKey}
        columns={columns}
        dataSource={shown}
        pagination={false}
        scroll={{ x: 'max-content' }}
        summary={
          currency === ALL_CURRENCIES
            ? undefined // amounts of different currencies are not added
            : () =>
                totalRow([
                  null,
                  null,
                  null,
                  null,
                  fmtNum(sumOf(shown, (r) => r.amount)),
                  fmtNum(sumOf(shown, (r) => r.events)),
                  fmtNum(sumOf(shown, (r) => r.eventsNoAmount)),
                ])
        }
      />
    </div>
  );
};

interface Props {
  entry: TypedEntry<EconomyRow[]>;
  onRetry?: () => void;
  initialCurrency?: string;
}

/** Soft currency earned and spent, with a currency filter. */
export const EconomyTab: React.FC<Props> = ({ entry, onRetry, initialCurrency }) => (
  <TabBody entry={entry} onRetry={onRetry}>
    {(rows) => <Content rows={rows} initialCurrency={initialCurrency} />}
  </TabBody>
);
